import type { ColumbusPlannerTicketModel } from '@/api/generated/schemas';
import { ChevronsDown, ChevronsUp, Coins, PlaneLanding, PlaneTakeoff, Users } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GroupCost, PaxCost, TermPlan } from '../../columbusTypes';
import { TicketsList } from '../../passengers/components/TicketsList';

interface Props {
  planKey: string;
  plan: TermPlan;
  groupCostNames: Pick<GroupCost, 'name'>[];
  paxCostNames: Pick<PaxCost, 'name'>[];
  maxTariffsCount: number;
  tickets?: ColumbusPlannerTicketModel[];

  onUpdateTariff: (index: number, field: 'pax' | 'retailPrice', value: number | null) => void;
  onAddTariff: () => void;
  onRemoveTariff: (index: number) => void;

  onUpdateGroupCostName: (index: number, name: string) => void;
  onUpdateGroupCostPrice: (index: number, price: number | null) => void;
  onAddGroupCost: () => void;
  onRemoveGroupCost: (index: number) => void;

  onUpdatePaxCostName: (index: number, name: string) => void;
  onUpdatePaxCostPrice: (index: number, price: number | null) => void;
  onAddPaxCost: () => void;
  onRemovePaxCost: (index: number) => void;

  onUpdateTicketPrice: (field: 'depTicketPrice' | 'retTicketPrice', value: number | null) => void;
  onBuyTickets?: () => void;
}

function SmallNumInput({
  value,
  placeholder,
  onChange,
  testId,
}: {
  value: number | null;
  placeholder?: string;
  onChange: (v: number | null) => void;
  testId?: string;
}) {
  return (
    <input
      type="number"
      data-testid={testId}
      className="border-phx-border-2 bg-phx-surface w-12 rounded border px-1 py-px text-right text-xs"
      placeholder={placeholder ?? ''}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    />
  );
}

function MidNumInput({
  value,
  placeholder,
  onChange,
  testId,
}: {
  value: number | null;
  placeholder?: string;
  onChange: (v: number | null) => void;
  testId?: string;
}) {
  return (
    <input
      type="number"
      data-testid={testId}
      className="border-phx-border-2 bg-phx-surface w-20 rounded border px-1 py-px text-right text-xs"
      placeholder={placeholder ?? ''}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    />
  );
}

function BoxHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-phx-text-muted flex items-center gap-1 px-1.5 py-0.5 text-xs font-semibold tracking-wide uppercase">
      {children}
    </div>
  );
}

function BoxBodyRow({ idx, children }: { idx: number; children: React.ReactNode }) {
  return (
    <div
      className={`border-phx-border-2 border-t border-solid px-1.5 py-0.5 ${
        idx % 2 === 0 ? 'bg-phx-surface-2' : 'bg-phx-surface'
      }`}
    >
      {children}
    </div>
  );
}

function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="text-phx-primary text-xs hover:underline" onClick={onClick}>
      + {label}
    </button>
  );
}

export function PlanEdit({
  planKey,
  plan,
  groupCostNames,
  paxCostNames,
  maxTariffsCount,
  tickets,
  onUpdateTariff,
  onAddTariff,
  onUpdateGroupCostName,
  onUpdateGroupCostPrice,
  onAddGroupCost,
  onUpdatePaxCostName,
  onUpdatePaxCostPrice,
  onAddPaxCost,
  onUpdateTicketPrice,
  onBuyTickets,
}: Props) {
  const { t } = useTranslation('columbus-planner');
  const [showTickets, setShowTickets] = useState(false);
  const tariffsCount = plan.tariffs.length;
  const hasTickets = !!tickets && tickets.length > 0;

  return (
    <div className="space-y-2" data-testid={`plan-edit-${planKey}`}>
      {/* Row 1: Tariffs + Group Costs + Pax Costs side by side */}
      <div className="flex space-x-5">
        {/* Tariffs */}
        <div>
          <BoxHeader>
            <span>{t('plan.tariffs')}</span>
            {tariffsCount < maxTariffsCount && (
              <AddButton label={t('plan.add')} onClick={onAddTariff} />
            )}
          </BoxHeader>
          {plan.tariffs.map((tariff, idx) => (
            <BoxBodyRow key={idx} idx={idx}>
              <div className="flex items-center gap-1">
                <div className="text-phx-text-faint w-4 text-xs">{idx + 1}</div>
                <SmallNumInput value={tariff.pax} onChange={(v) => onUpdateTariff(idx, 'pax', v)} />
                <Users className="text-phx-text-faint h-3 w-3" />
                <MidNumInput
                  value={tariff.retailPrice}
                  onChange={(v) => onUpdateTariff(idx, 'retailPrice', v)}
                />
                <Coins className="text-phx-text-faint h-3 w-3" />
              </div>
            </BoxBodyRow>
          ))}
        </div>

        {/* Group Costs */}
        <div>
          <BoxHeader>
            <span>{t('plan.groupCosts')}</span>
            <AddButton label={t('plan.add')} onClick={onAddGroupCost} />
          </BoxHeader>
          {groupCostNames.map((gc, idx) => (
            <BoxBodyRow key={idx} idx={idx}>
              <div className="flex items-center gap-1">
                <input
                  className="border-phx-border-2 bg-phx-surface w-24 rounded border px-1 py-px text-xs"
                  value={gc.name}
                  placeholder={t('plan.namePlaceholder')}
                  onChange={(e) => onUpdateGroupCostName(idx, e.target.value)}
                />
                <MidNumInput
                  value={plan.groupCosts[idx]?.price ?? null}
                  onChange={(v) => onUpdateGroupCostPrice(idx, v)}
                />
                <Coins className="text-phx-text-faint h-3 w-3" />
              </div>
            </BoxBodyRow>
          ))}
        </div>

        {/* Pax Costs */}
        <div>
          <BoxHeader>
            <span>{t('plan.paxCosts')}</span>
            <AddButton label={t('plan.add')} onClick={onAddPaxCost} />
          </BoxHeader>
          {paxCostNames.map((pc, idx) => (
            <BoxBodyRow key={idx} idx={idx}>
              <div className="flex items-center gap-1">
                <input
                  className="border-phx-border-2 bg-phx-surface w-24 rounded border px-1 py-px text-xs"
                  value={pc.name}
                  placeholder={t('plan.namePlaceholder')}
                  onChange={(e) => onUpdatePaxCostName(idx, e.target.value)}
                />
                <MidNumInput
                  value={plan.paxCosts[idx]?.price ?? null}
                  onChange={(v) => onUpdatePaxCostPrice(idx, v)}
                />
                <Coins className="text-phx-text-faint h-3 w-3" />
                <span className="text-phx-text-faint text-xs">{t('plan.perPerson')}</span>
              </div>
            </BoxBodyRow>
          ))}
        </div>
      </div>

      {/* Ticket Costs */}
      <div>
        <BoxHeader>{t('plan.ticketCosts')}</BoxHeader>
        <BoxBodyRow idx={0}>
          <div className="flex items-center gap-2">
            {hasTickets && (
              <button
                type="button"
                className={`hover:bg-phx-surface-3 rounded p-0.5 transition-colors ${showTickets ? 'text-phx-primary' : 'text-phx-text-muted'}`}
                onClick={() => setShowTickets((s) => !s)}
                title={showTickets ? t('plan.hideTickets') : t('plan.showTickets')}
              >
                {showTickets ? (
                  <ChevronsUp className="h-3 w-3" />
                ) : (
                  <ChevronsDown className="h-3 w-3" />
                )}
              </button>
            )}
            <div className="flex items-center gap-1">
              <PlaneTakeoff className="text-phx-text-faint h-3 w-3" />
              <MidNumInput
                value={plan.depTicketPrice}
                onChange={(v) => onUpdateTicketPrice('depTicketPrice', v)}
                testId="dep-ticket-price"
              />
              <Coins className="text-phx-text-faint h-3 w-3" />
            </div>
            <div className="flex items-center gap-1">
              <PlaneLanding className="text-phx-text-faint h-3 w-3" />
              <MidNumInput
                value={plan.retTicketPrice}
                onChange={(v) => onUpdateTicketPrice('retTicketPrice', v)}
                testId="ret-ticket-price"
              />
              <Coins className="text-phx-text-faint h-3 w-3" />
            </div>
            {onBuyTickets && (
              <button
                type="button"
                className="border-phx-primary text-phx-primary hover:bg-phx-primary-tint rounded border px-1.5 py-px text-xs"
                onClick={onBuyTickets}
              >
                {t('plan.buyTickets')}
              </button>
            )}
          </div>
          {hasTickets && showTickets && <TicketsList tickets={tickets!} />}
        </BoxBodyRow>
      </div>
    </div>
  );
}
